import { useEffect, useRef, useState } from "react";
import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from "react";
import { ActionIcon, Box, Group, Paper, Text } from "@mantine/core";
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconGripVertical,
  IconMinus,
  IconX,
} from "@tabler/icons-react";

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

interface ResizeStart {
  mouseX: number;
  mouseY: number;
  windowX: number;
  windowY: number;
  width: number;
  height: number;
  direction: ResizeDirection;
}

interface DragStart {
  mouseX: number;
  mouseY: number;
  windowX: number;
  windowY: number;
}

const MIN_WINDOW_WIDTH = 520;
const MIN_WINDOW_HEIGHT = 320;
const DEFAULT_WINDOW_SIZE = { width: 1120, height: 780 };

function calculateResizedWindow(resizeStart: ResizeStart, event: MouseEvent) {
  const deltaX = event.clientX - resizeStart.mouseX;
  const deltaY = event.clientY - resizeStart.mouseY;
  const growsEast = resizeStart.direction.includes("e");
  const growsWest = resizeStart.direction.includes("w");
  const growsSouth = resizeStart.direction.includes("s");
  const growsNorth = resizeStart.direction.includes("n");

  let nextWidth =
    resizeStart.width + (growsEast ? deltaX : 0) - (growsWest ? deltaX : 0);
  let nextHeight =
    resizeStart.height + (growsSouth ? deltaY : 0) - (growsNorth ? deltaY : 0);

  nextWidth = Math.max(
    MIN_WINDOW_WIDTH,
    Math.min(nextWidth, globalThis.innerWidth - 16),
  );
  nextHeight = Math.max(
    MIN_WINDOW_HEIGHT,
    Math.min(nextHeight, globalThis.innerHeight - 16),
  );

  const nextX = growsWest
    ? resizeStart.windowX + resizeStart.width - nextWidth
    : resizeStart.windowX;
  const nextY = growsNorth
    ? resizeStart.windowY + resizeStart.height - nextHeight
    : resizeStart.windowY;

  return {
    position: {
      x: Math.max(8, Math.min(nextX, globalThis.innerWidth - MIN_WINDOW_WIDTH)),
      y: Math.max(8, Math.min(nextY, globalThis.innerHeight - 80)),
    },
    size: {
      width: nextWidth,
      height: nextHeight,
    },
  };
}

function calculateWindowMovement(
  resizeStart: ResizeStart | null,
  dragStart: DragStart | null,
  event: MouseEvent,
) {
  if (resizeStart) {
    return calculateResizedWindow(resizeStart, event);
  }
  if (!dragStart) return null;

  const nextX = dragStart.windowX + event.clientX - dragStart.mouseX;
  const nextY = dragStart.windowY + event.clientY - dragStart.mouseY;

  return {
    position: {
      x: Math.max(8, Math.min(nextX, globalThis.innerWidth - 260)),
      y: Math.max(8, Math.min(nextY, globalThis.innerHeight - 80)),
    },
    size: null,
  };
}

function getPopupSize(
  isMaximized: boolean,
  isMinimized: boolean,
  windowSize: typeof DEFAULT_WINDOW_SIZE,
) {
  if (isMaximized) {
    return {
      width: globalThis.innerWidth - 32,
      height: globalThis.innerHeight - 32,
    };
  }

  return {
    width: isMinimized ? 360 : windowSize.width,
    height: isMinimized ? 40 : windowSize.height,
  };
}

const resizeHandles: Array<{
  direction: ResizeDirection;
  style: CSSProperties;
}> = [
  {
    direction: "n",
    style: { top: -4, left: 12, right: 12, height: 8, cursor: "ns-resize" },
  },
  {
    direction: "s",
    style: {
      bottom: -4,
      left: 12,
      right: 12,
      height: 8,
      cursor: "ns-resize",
    },
  },
  {
    direction: "e",
    style: { top: 12, right: -4, bottom: 12, width: 8, cursor: "ew-resize" },
  },
  {
    direction: "w",
    style: { top: 12, left: -4, bottom: 12, width: 8, cursor: "ew-resize" },
  },
  {
    direction: "ne",
    style: {
      top: -6,
      right: -6,
      width: 14,
      height: 14,
      cursor: "nesw-resize",
    },
  },
  {
    direction: "nw",
    style: {
      top: -6,
      left: -6,
      width: 14,
      height: 14,
      cursor: "nwse-resize",
    },
  },
  {
    direction: "se",
    style: {
      right: -6,
      bottom: -6,
      width: 14,
      height: 14,
      cursor: "nwse-resize",
    },
  },
  {
    direction: "sw",
    style: {
      left: -6,
      bottom: -6,
      width: 14,
      height: 14,
      cursor: "nesw-resize",
    },
  },
];

export function useSettingsPopupWindow() {
  const [windowPosition, setWindowPosition] = useState({ x: 72, y: 48 });
  const [windowSize, setWindowSize] = useState(DEFAULT_WINDOW_SIZE);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const restoreWindowRef = useRef({
    position: { x: 72, y: 48 },
    size: DEFAULT_WINDOW_SIZE,
  });
  const dragStartRef = useRef<DragStart | null>(null);
  const resizeStartRef = useRef<ResizeStart | null>(null);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const next = calculateWindowMovement(
        resizeStartRef.current,
        dragStartRef.current,
        event,
      );
      if (!next) return;

      setWindowPosition(next.position);
      if (next.size) {
        setWindowSize(next.size);
      }
    };

    const handleMouseUp = () => {
      dragStartRef.current = null;
      resizeStartRef.current = null;
    };

    globalThis.addEventListener("mousemove", handleMouseMove);
    globalThis.addEventListener("mouseup", handleMouseUp);

    return () => {
      globalThis.removeEventListener("mousemove", handleMouseMove);
      globalThis.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const startDrag = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (isMaximized) return;

    dragStartRef.current = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      windowX: windowPosition.x,
      windowY: windowPosition.y,
    };
  };

  const startResize = (
    event: ReactMouseEvent<HTMLDivElement>,
    direction: ResizeDirection,
  ) => {
    if (isMaximized || isMinimized) return;
    event.stopPropagation();
    event.preventDefault();

    resizeStartRef.current = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      windowX: windowPosition.x,
      windowY: windowPosition.y,
      width: windowSize.width,
      height: windowSize.height,
      direction,
    };
  };

  const handleMinimize = () => {
    setIsMinimized((value) => !value);
    setIsMaximized(false);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setIsMaximized((value) => {
      if (value) {
        setWindowPosition(restoreWindowRef.current.position);
        setWindowSize(restoreWindowRef.current.size);
        return false;
      }

      restoreWindowRef.current = {
        position: windowPosition,
        size: windowSize,
      };
      setWindowPosition({ x: 16, y: 16 });
      setWindowSize({
        width: globalThis.innerWidth - 32,
        height: globalThis.innerHeight - 32,
      });
      return true;
    });
  };

  return {
    isMinimized,
    isMaximized,
    popupSize: getPopupSize(isMaximized, isMinimized, windowSize),
    windowPosition,
    resizeHandles,
    startDrag,
    startResize,
    handleMinimize,
    handleMaximize,
  };
}

type SettingsPopupWindowState = ReturnType<typeof useSettingsPopupWindow>;

interface SettingsPopupWindowProps
  extends Pick<
    SettingsPopupWindowState,
    | "isMinimized"
    | "isMaximized"
    | "popupSize"
    | "windowPosition"
    | "resizeHandles"
    | "startDrag"
    | "startResize"
    | "handleMinimize"
    | "handleMaximize"
  > {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export default function SettingsPopupWindow({
  title,
  isMinimized,
  isMaximized,
  popupSize,
  windowPosition,
  resizeHandles,
  startDrag,
  startResize,
  handleMinimize,
  handleMaximize,
  onClose,
  children,
}: Readonly<SettingsPopupWindowProps>) {
  return (
    <Paper
      withBorder
      shadow="xl"
      radius="md"
      style={{
        position: "fixed",
        left: windowPosition.x,
        top: windowPosition.y,
        zIndex: 1000,
        width: popupSize.width,
        height: popupSize.height,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Group
        justify="space-between"
        px="sm"
        py={6}
        bg="var(--mantine-color-blue-6)"
        c="white"
        style={{
          cursor: isMaximized ? "default" : "move",
          userSelect: "none",
        }}
        onMouseDown={startDrag}
      >
        <Group gap={6}>
          <IconGripVertical size={16} />
          <Text size="sm" fw={700}>
            {title}
          </Text>
        </Group>
        <Group gap={4} onMouseDown={(event) => event.stopPropagation()}>
          <ActionIcon
            variant="subtle"
            color="white"
            aria-label={isMinimized ? "Restore" : "Minimize"}
            onClick={handleMinimize}
          >
            <IconMinus size={18} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="white"
            aria-label={isMaximized ? "Restore" : "Maximize"}
            onClick={handleMaximize}
          >
            {isMaximized ? (
              <IconArrowsMinimize size={18} />
            ) : (
              <IconArrowsMaximize size={18} />
            )}
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="white"
            aria-label="Close"
            onClick={onClose}
          >
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Group>
      {!isMinimized && (
        <Box p="sm" style={{ flex: 1, minHeight: 0 }}>
          {children}
        </Box>
      )}
      {!isMinimized &&
        !isMaximized &&
        resizeHandles.map(({ direction, style }) => (
          <Box
            key={direction}
            onMouseDown={(event) => startResize(event, direction)}
            style={{
              position: "absolute",
              zIndex: 2,
              ...style,
            }}
          />
        ))}
    </Paper>
  );
}
